<template>
  <!-- Source deleted (saved filter set no longer exists) -->
  <div v-if="config.unresolved" class="tile-state">
    <GoblinIcon variant="error" :size="28" />
    <p class="state-title">Source deleted</p>
    <p class="state-sub">The saved filter set behind this tile no longer exists.</p>
  </div>

  <div v-else class="logs-tile" :style="{ maxHeight: height + 'px' }">
    <p v-if="liveCapped" class="capped-note">
      Live updates paused — a dashboard refreshes at most {{ MAX_LIVE_LOGS_TILES }} log tiles.
    </p>

    <div v-if="error" class="tile-state">
      <GoblinIcon variant="error" :size="28" />
      <p class="state-sub">{{ error }}</p>
    </div>

    <div v-else-if="loading && !logs.length" class="tile-state">
      <p class="state-sub">Loading…</p>
    </div>

    <div v-else-if="!logs.length" class="tile-state">
      <GoblinIcon variant="empty" :size="28" />
      <p class="state-sub">No matching logs in this range.</p>
    </div>

    <div v-else class="rows">
      <LogRow
        v-for="row in logs"
        :key="row.id"
        :row="row"
        :expanded="expandedId === row.id"
        @toggle="expandedId = expandedId === row.id ? null : row.id"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { api, ApiError, type LogRow as LogRowT, type ResolvedLogsTileConfig } from '../../api.js';
import { logsTileParams, MAX_LIVE_LOGS_TILES } from '../../lib/dashboard-layout.js';
import { cachedTileQuery } from '../../lib/tile-query-cache.js';
import LogRow from '../LogRow.vue';
import GoblinIcon from '../GoblinIcon.vue';

const props = withDefaults(
  defineProps<{
    config: ResolvedLogsTileConfig;
    /** The dashboard clock's window (§3.4). */
    from: number;
    to: number;
    /**
     * Live-cap (amendments §5): at most 4 log tiles per dashboard follow the
     * live tick; capped ones keep their data and show a note. A range change
     * (window SPAN change) still refetches even when capped — that's a user
     * action, not the live poll.
     */
    liveCapped?: boolean;
    height?: number;
  }>(),
  { liveCapped: false, height: 360 },
);

const emit = defineEmits<{ unauthorized: [] }>();

const logs = ref<LogRowT[]>([]);
const loading = ref(false);
const error = ref('');
const expandedId = ref<number | null>(null);
let seq = 0;

async function fetchTile() {
  const params = logsTileParams(props.config, props.from, props.to);
  if (!params) return;
  const mySeq = ++seq;
  loading.value = true;
  try {
    const res = await cachedTileQuery(`logs:${JSON.stringify(params)}`, () => api.query(params));
    if (mySeq !== seq) return;
    logs.value = res.logs;
    error.value = '';
  } catch (e) {
    if (mySeq !== seq) return;
    logs.value = [];
    error.value = e instanceof ApiError ? e.message : 'Query failed.';
    if (e instanceof ApiError && e.unauthorized) emit('unauthorized');
  } finally {
    if (mySeq === seq) loading.value = false;
  }
}

// Refetch when the clock window moves — but a capped tile ignores pure live
// ticks (same span, new `to`) and only follows span changes (range switch).
watch(
  () => [props.from, props.to, props.config] as [number, number, ResolvedLogsTileConfig],
  ([from, to, config], old) => {
    if (props.liveCapped && old) {
      const [oldFrom, oldTo, oldConfig] = old;
      const spanChanged = to - from !== oldTo - oldFrom;
      if (!spanChanged && config === oldConfig) return;
    }
    fetchTile();
  },
  { immediate: true },
);

// Freshly un-capped (a tile above was removed/reordered): catch up right away.
watch(
  () => props.liveCapped,
  (capped) => {
    if (!capped) fetchTile();
  },
);
</script>

<style scoped>
.logs-tile {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.rows {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.capped-note {
  margin: 0;
  padding: var(--lg-space-1) var(--lg-space-3);
  font-size: var(--lg-fs-xs);
  color: var(--lg-warn);
  border-bottom: 1px solid var(--lg-border-faint);
  flex-shrink: 0;
}
.tile-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-1);
  padding: var(--lg-space-4);
  min-height: 120px;
  color: var(--lg-text-faint);
  text-align: center;
}
.state-title {
  margin: 0;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  color: var(--lg-text-dim);
}
.state-sub {
  margin: 0;
  font-size: var(--lg-fs-xs);
  max-width: 32ch;
}
</style>
